import { axiosApiClient } from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/reports";

class ReportsService {
  // Dobijanje svih izveštaja sa filterima
  getReports = async (filters = {}, pagination = {}) => {
    try {
      const { page = 1, limit = 5 } = pagination;

      const response = await axiosApiClient.get(`${BASE_URL}/`, {
        params: {
          type: filters.type,
          scope: filters.scope,
          status: filters.status,
          bandId: filters.bandId,
          festivalId: filters.festivalId,
          month: filters.month,
          page,
          limit,
        },
      });

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching reports.");
    }
  };

  // Generiranje novog izveštaja
  generateReport = async (reportData) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/`, reportData);

      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating report.");
    }
  };

  // Generisanje izveštaja za period (više meseci odjednom)
  generatePeriodReports = async (periodData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-period`,
        periodData
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating period reports.");
    }
  };

  // Dobijanje pojedinačnog izveštaja
  getReport = async (reportId) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${reportId}`);

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching report.");
    }
  };

  // Download izveštaja
  downloadReport = async (reportId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${reportId}/download`,
        {
          responseType: "blob", // Važno za file download
          // Dodaj ove header-e da osiguraš pravilno parsiranje
          headers: {
            Accept:
              "application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*",
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Kreiraj blob sa eksplicitnim MIME type-om
      const contentType = response.headers["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });

      // Kreiraj download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Dobij filename iz headers - poboljšana logika
      let filename = "mesecni_izvestaj.pdf"; // default
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        // Probaj različite pattern-e
        const patterns = [
          /filename="([^"]+)"/i,
          /filename=([^;]+)/i,
          /filename\*=UTF-8''([^;]+)/i,
        ];

        for (const pattern of patterns) {
          const match = contentDisposition.match(pattern);
          if (match) {
            filename = decodeURIComponent(match[1].trim());
            break;
          }
        }
      }

      // Osigurai da filename ima ekstenziju
      if (!filename.includes(".")) {
        const contentType = response.headers["content-type"];
        if (contentType?.includes("pdf")) {
          filename += ".pdf";
        } else if (contentType?.includes("spreadsheet")) {
          filename += ".xlsx";
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (err) {
      console.error("Download error details:", {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        headers: err.response?.headers,
        data: err.response?.data,
      });

      throw handleError(err, "Error occurred while downloading report.");
    }
  };

  // Dobijanje izveštaja za bend/festival panel (scope-ovano po ulogovanom korisniku)
  getBandPanelReports = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/band-panel`);

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching band reports.");
    }
  };

  // Download izveštaja iz bend panela, isti blob-link pattern kao downloadReport
  downloadBandPanelReport = async (reportId, fileName) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/band-panel/${reportId}/download`,
        {
          responseType: "blob", // Važno za file download
          headers: {
            Accept:
              "application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*",
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Kreiraj blob sa eksplicitnim MIME type-om
      const contentType = response.headers["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });

      // Kreiraj download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Dobij filename iz headers, fallback na fileName iz liste izveštaja
      let filename = fileName || "mesecni_izvestaj.pdf";
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const patterns = [
          /filename="([^"]+)"/i,
          /filename=([^;]+)/i,
          /filename\*=UTF-8''([^;]+)/i,
        ];

        for (const pattern of patterns) {
          const match = contentDisposition.match(pattern);
          if (match) {
            filename = decodeURIComponent(match[1].trim());
            break;
          }
        }
      }

      // Osiguraj da filename ima ekstenziju
      if (!filename.includes(".")) {
        if (contentType.includes("pdf")) {
          filename += ".pdf";
        } else if (contentType.includes("spreadsheet")) {
          filename += ".xlsx";
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (err) {
      throw handleError(err, "Error occurred while downloading report.");
    }
  };

  // Brisanje izveštaja
  deleteReport = async (reportId) => {
    try {
      const response = await axiosApiClient.delete(`${BASE_URL}/${reportId}`);

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting report.");
    }
  };

  // Dobijanje statistika izveštaja
  getReportStats = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/stats`);

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching report statistics."
      );
    }
  };

  // Dobijanje logova slanja emailova
  getEmailLogs = async (params = {}) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/email-logs`, {
        params,
      });
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching email logs.");
    }
  };

  // Preview poslatog email izveštaja
  getEmailLogPreview = async (logId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/email-logs/${logId}/preview`
      );
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching email preview.");
    }
  };

  // Preuzimanje PDF priloga iz email loga
  downloadEmailLogPdf = async (logId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/email-logs/${logId}/pdf`,
        { responseType: "blob" },
      );
      if (response.status !== 200) throw new Error();
      return response;
    } catch (err) {
      throw handleError(err, "Error occurred while downloading email PDF.");
    }
  };

  // Ponovno slanje izveštaja bendu ili festivalu
  resendReport = async (ownerType, ownerId, data) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/resend/${ownerType}/${ownerId}`,
        data
      );
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while resending report.");
    }
  };

  // Polling za status izveštaja
  pollReportStatus = async (reportId, maxAttempts = 30, interval = 2000) => {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const poll = async () => {
        try {
          attempts++;
          const response = await this.getReport(reportId);
          const report = response.data;

          if (report.status === "completed" || report.status === "failed") {
            resolve(report);
          } else if (attempts >= maxAttempts) {
            reject(new Error("Report generation timeout"));
          } else {
            setTimeout(poll, interval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  };
}

const reportsService = new ReportsService();
export default reportsService;
